import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";

/**
 * The ticket panel lives in the URL — `?tk=ticket:123` (one ticket),
 * `?tk=customer:45` (a customer's tickets), `?tk=new:45` (a new ticket for
 * them) or `?tk=new` (a new ticket: first, who for) — so it survives a reload,
 * can be shared, and the browser's Back closes it or steps back through it.
 *
 * One docked panel at a time: opening it closes the WhatsApp panel (`wa`), and
 * the WhatsApp panel's own `open` closes this one. Both push a history entry,
 * so Back returns to the panel that was open before.
 */
export type TicketDockTarget =
  | { kind: "ticket"; id: number }
  | { kind: "customer"; id: number }
  | { kind: "new"; id: number | null };

export const TICKET_DOCK_PARAM = "tk";

export function parseTicketDockTarget(value: string | null): TicketDockTarget | null {
  if (value === "new") return { kind: "new", id: null };
  const match = /^(ticket|customer|new):(\d+)$/.exec(value ?? "");
  if (!match) return null;
  const id = Number(match[2]);
  if (!Number.isSafeInteger(id) || id <= 0) return null;
  return { kind: match[1] as TicketDockTarget["kind"], id };
}

const serialize = (target: TicketDockTarget) =>
  target.id === null ? target.kind : `${target.kind}:${target.id}`;

export function useTicketDock() {
  const [params, setParams] = useSearchParams();
  const target = parseTicketDockTarget(params.get(TICKET_DOCK_PARAM));

  /** `replace`: a step not worth going Back to — the form a ticket was just made from. */
  const open = useCallback(
    (next: TicketDockTarget, options?: { replace?: boolean }) =>
      setParams(
        (current) => {
          const updated = new URLSearchParams(current);
          updated.set(TICKET_DOCK_PARAM, serialize(next));
          updated.delete("wa");
          updated.delete("waThread");
          return updated;
        },
        { replace: options?.replace ?? false },
      ),
    [setParams],
  );

  const close = useCallback(
    () =>
      setParams((current) => {
        const updated = new URLSearchParams(current);
        updated.delete(TICKET_DOCK_PARAM);
        return updated;
      }),
    [setParams],
  );

  return { target, open, close };
}
